import MDEdit from "@uiw/react-md-editor";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { CSSProperties, PureComponent, ReactNode } from "react";
import { ImageLibraryService } from "../../../services/image-library-service";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { CommonProps } from "../common-props";

interface MDEditorProps {
  value: string;
  onChange?: (value: string) => void;
  style?: CSSProperties;
  theme?: "light" | "dark";
  simplified?: boolean;
}

interface MDEditorState {
  content: string;
  tempBase64: ITempBase64[];
}

interface ITempBase64 {
  id: string;
  content: string;
}

interface TextState {
  text: string;
  selectedText: string;
  selection: { start: number; end: number };
}

interface TextApi {
  replaceSelection: (text: string) => void;
}

declare let window: Window & { kuika: any };

class MDEditor extends PureComponent<MDEditorProps & CommonProps, MDEditorState> {
  refKey: Guid = Guid.create();

  isDesignTime: boolean = window.kuika?.isDesignTime;

  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kmdeditor_${this.uniqueKey}`);

  constructor(props: MDEditorProps) {
    super(props);
    this.state = {
      content: "",
      tempBase64: []
    };
  }

  clickAddImageButton = (e: any) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".png, .jpg, .svg";

    input.onchange = (e: any) => {
      const file = e.target.files[0];

      const reader = new FileReader();

      reader.onload = (readerEvent: any) => {
        const result = reader.result?.toString();
        if (result) {
          this.updateImageBase64(this.state.content, result);
        }
      };
      reader.readAsDataURL(file);
    };

    input.click();
  };

  clickHandleEditOrPreview = (val: string) => {
    if (val === "edit") {
      this.handleEdit();
    } else if (val === "preview") {
      this.handlePreview();
    }
  };

  handleEdit = () => {};

  handlePreview = () => {};

  componentDidMount = () => {
    this.setDynamicStyle();
    const addImageButton = window.document.evaluate(
      `//*[@id="${this.refKey}"]/div/div[1]/ul/li[12]/button`,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;
    const editButton = window.document.evaluate(
      `//*[@id="${this.refKey}"]/div/div[1]/ul/li[18]/button`,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;
    const previewButton = window.document.evaluate(
      `//*[@id="${this.refKey}"]/div/div[1]/ul/li[20]/button`,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;

    if (addImageButton) {
      addImageButton.addEventListener("click", this.clickAddImageButton);
    }
    if (editButton) {
      editButton.addEventListener("click", () => this.clickHandleEditOrPreview("edit"));
    }
    if (previewButton) {
      previewButton.addEventListener("click", () => this.clickHandleEditOrPreview("preview"));
    }
  };

  componentWillUnmount = () => {
    const addImageButton = window.document.evaluate(
      `//*[@id="${this.refKey}"]/div/div[1]/ul/li[12]/button`,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;
    if (addImageButton) {
      addImageButton.removeEventListener("click", this.clickAddImageButton);
    }
  };

  emitOnChange = (value: string): void => {
    if (!this.props.onChange) return;
    this.props.onChange(value);
  };

  handleChangeEditor = (value?: string) => {
    this.setState({ content: !_.isEmpty(value) ? value : "" }, () => this.emitOnChange(value));
  };

  updateImageBase64 = (value: string, image: string) => {
    if (value) {
      const searchString = "https://example.com/your-image.png";
      if (this.state.content.indexOf(searchString) > -1 && image.length > 0) {
        const tempObject = {} as ITempBase64;
        tempObject.id = Guid.create().toString();
        tempObject.content = image;
        ImageLibraryService.SaveResource(tempObject);
        value = value.replace(searchString, `{KUIKARESOURCES/}${tempObject.id}`);
        this.setState({ content: value, tempBase64: [...this.state.tempBase64, tempObject] });
        if (this.props.onChange) {
          this.props.onChange(value);
        }
      }
    }
  };

  getStyle = (): CSSProperties => {
    const {
      alignItems,
      fontSize,
      fontWeight,
      fontFamily,
      fontStyle,
      letterSpacing,
      lineHeight,
      textAlign,
      textDecoration,
      textTransform,
      color,
      ...restStyle
    } = {
      ...this.props.style
    };
    const style: CSSProperties = restStyle;

    if (style.display == "inline") {
      style.display = "inline-block";
    }

    return style;
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    result += `.${className.trim()} .w-md-editor {
        border: none !important;
      }`;

    if (this.props.simplified) {
      result += `
        .${className.trim()} .w-md-editor-toolbar > ul:first-child > li:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) {
          display: none !important;
        }
        .${className.trim()} .w-md-editor-toolbar > ul:last-child {
          display: none !important;
        }
      `;
    }

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  componentDidUpdate(prevProps: MDEditorProps) {
    if (prevProps.simplified !== this.props.simplified) {
      this.setDynamicStyle();
    }
  }

  getClassName = () => {
    let result = "";
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result}kmdeditor_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getValue = () => {
    return this.state?.content && this.state.content !== "" ? this.state.content : this.props.value;
  };

  getSimplifiedCommands = () => {
    return {
      commands: [
        {
          name: "bold",
          keyCommand: "bold",
          buttonProps: { "aria-label": "Bold" },
          icon: (
            <svg role="img" width="12" height="12" viewBox="0 0 384 512">
              <path
                fill="currentColor"
                d="M304.793 243.891c33.639-18.537 53.657-54.16 53.657-95.693 0-48.236-26.25-87.626-68.626-104.179C265.138 34.01 240.849 32 209.661 32H24c-8.837 0-16 7.163-16 16v33.049c0 8.837 7.163 16 16 16h33.113v318.53H24c-8.837 0-16 7.163-16 16V464c0 8.837 7.163 16 16 16h195.69c24.203 0 44.834-1.289 66.866-7.584C337.52 457.193 376 410.647 376 350.014c0-52.168-26.573-91.684-71.207-106.123zM142.217 100.809h67.444c16.294 0 27.536 2.019 37.525 6.717 15.828 8.479 24.906 26.502 24.906 49.446 0 35.029-20.32 56.79-53.029 56.79h-76.846V100.809zm112.642 305.475c-10.14 4.056-22.677 4.907-31.409 4.907h-81.233V281.943h84.367c39.645 0 63.057 25.38 63.057 63.057.001 28.425-13.66 52.483-34.782 61.284z"
              ></path>
            </svg>
          ),
          execute: (state: TextState, api: TextApi) => {
            let modifyText = `**${state.selectedText}**`;
            if (!state.selectedText) {
              modifyText = "**bold text**";
            }
            api.replaceSelection(modifyText);
          }
        },
        {
          name: "italic",
          keyCommand: "italic",
          buttonProps: { "aria-label": "Italic" },
          icon: (
            <svg data-name="italic" width="12" height="12" role="img" viewBox="0 0 320 512">
              <path
                fill="currentColor"
                d="M204.758 416h-33.849l62.092-320h40.725a16 16 0 0 0 15.704-12.937l6.242-32C297.599 41.184 290.034 32 279.968 32H120.235a16 16 0 0 0-15.704 12.937l-6.242 32C96.362 86.816 103.927 96 113.993 96h33.846l-62.09 320H46.278a16 16 0 0 0-15.704 12.935l-6.245 32C22.402 470.815 29.967 480 40.034 480h158.479a16 16 0 0 0 15.704-12.935l6.245-32c1.927-9.88-5.638-19.065-15.704-19.065z"
              ></path>
            </svg>
          ),
          execute: (state: TextState, api: TextApi) => {
            let modifyText = `*${state.selectedText}*`;
            if (!state.selectedText) {
              modifyText = "*italic text*";
            }
            api.replaceSelection(modifyText);
          }
        },
        {
          name: "underline",
          keyCommand: "underline",
          buttonProps: { "aria-label": "Underline" },
          icon: (
            <svg data-name="underline" width="12" height="12" role="img" viewBox="0 0 448 512">
              <path
                fill="currentColor"
                d="M32 64h32v160c0 88.22 71.78 160 160 160s160-71.78 160-160V64h32a16 16 0 0 0 16-16V16a16 16 0 0 0-16-16H272a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h32v160a80 80 0 0 1-160 0V64h32a16 16 0 0 0 16-16V16a16 16 0 0 0-16-16H32a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16zm400 384H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16z"
              ></path>
            </svg>
          ),
          execute: (state: TextState, api: TextApi) => {
            let modifyText = `<u>${state.selectedText}</u>`;
            if (!state.selectedText) {
              modifyText = "<u>underlined text</u>";
            }
            api.replaceSelection(modifyText);
          }
        }
      ],
      extraCommands: []
    };
  };

  render(): ReactNode {
    const { theme = "light", simplified = false } = this.props;

    const editorProps = simplified
      ? {
          ...this.props,
          ...this.getSimplifiedCommands()
        }
      : this.props;

    return (
      <div id={this.refKey.toString()} className={this.getClassName()} data-color-mode={theme}>
        <MDEdit
          {...editorProps}
          height={this.props.style?.height as number}
          style={this.getStyle()}
          onChange={this.handleChangeEditor}
          value={this.getValue()}
        />
      </div>
    );
  }
}

const mdEditor = withCommonEvents(MDEditor);
export { mdEditor as MDEditor };
